import { useCallback, useMemo } from "react";

import { DateTime } from "luxon";
import { useGlobalSetting } from "Store/GlobalSettingStore";

export const utcDateTime = (time?: string | Date | number | DateTime | null | undefined) => {
    let dt: DateTime;
    if (time === undefined || time === null) {
        dt = DateTime.utc();
    } else if (typeof time === "string") {
        dt = DateTime.fromISO(time, { zone: "utc" });
    } else if (typeof time === "number") {
        dt = DateTime.fromMillis(time, { zone: "utc" });
    } else if (time instanceof Date) {
        dt = DateTime.fromJSDate(time, { zone: "utc" });
    } else {
        dt = time.setZone("utc");
    }
    return dt;
};

export const useTimeZone = (zone?: string) => {
    const timeStore = useGlobalSetting("time");
    const currentZone = zone ?? timeStore.selectedTimeZone;
    const currentZoneAbbreviation = useMemo(() => DateTime.now().setZone(currentZone).offsetNameShort, [currentZone]);
    const currentZoneDateTime = useCallback(
        (time?: string | Date | number | DateTime | null | undefined) => utcDateTime(time).setZone(currentZone),
        [currentZone]
    );

    const format = useCallback(
        (formatOpts?: Intl.DateTimeFormatOptions) => (time?: string | Date | number | DateTime | null | undefined) =>
            currentZoneDateTime(time).toLocaleString(formatOpts),
        [currentZoneDateTime]
    );

    const formatTime = useMemo(() => format(DateTime.TIME_WITH_SHORT_OFFSET), [format]);
    const formatTime24Hours = useMemo(() => format(DateTime.TIME_24_WITH_SHORT_OFFSET), [format]);
    const formatTimeShort = useMemo(() => format({ hour: "2-digit", minute: "2-digit", hour12: false }), [format]);
    const formatDate = useMemo(() => format(DateTime.DATE_MED), [format]);
    const formatDateShort = useMemo(() => format({ month: "short", day: "2-digit" }), [format]);
    const formatDateTime = useMemo(() => format(DateTime.DATETIME_FULL), [format]);
    const formatDateTimeShort = useMemo(() => format({ month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }), [format]);
    const formatDateTimeShortWithSeconds = useMemo(() => format(DateTime.DATETIME_SHORT_WITH_SECONDS), [format]);
    const formatDateTimeShortWithZone = useMemo(
        () => format({ month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", timeZoneName: "short", hour12: false }),
        [format]
    );
    const formatMonth = useMemo(() => format({ month: "short", year: "numeric" }), [format]);
    const formatDateTimeLongWithZone = useMemo(
        () => format({ year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", timeZoneName: "short", hour12: false }),
        [format]
    );

    return {
        utcDateTime,
        currentZoneDateTime,
        format,
        formatTime,
        formatDate,
        formatDateShort,
        formatDateTime,
        formatDateTimeShort,
        formatDateTimeShortWithSeconds,
        formatDateTimeShortWithZone,
        formatMonth,
        formatDateTimeLongWithZone,
        formatTime24Hours,
        formatTimeShort,
        currentZone,
        currentZoneAbbreviation
    };
};
